/* eslint-disable no-unused-vars */
import {
  closestCenter,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEffect } from "react";
import { useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { AutoSizer, List, WindowScroller } from "react-virtualized";

import useScreenSize from "@/hooks/useScreenSize";
import InfiniteScroll from "@/shared-components/lists/components/InfiniteScroll";

import ArtworkRow from "./ArtworkRow";

import "./styles/VirtualList.css";

export default function VirtualList({
  items,
  onLoadMore,
  onClickRemove,
  onChangeMade,
}) {
  const screenSize = useScreenSize();
  const [cards, setCards] = useState(items.data);

  useEffect(() => {
    if(items != cards)
      setCards(items.data);
  }, [items]);

  useEffect(() => {
    onChangeMade(cards);
  }, [cards]);

  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 0 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    })
  );

  function deleteItem(itemToDelete) {
    const newItems = cards.filter((item) => item._id !== itemToDelete._id);

    setCards(newItems);
    onClickRemove(itemToDelete);
  }

  function handleSortEnd(sortedArtworks) {
    setCards(sortedArtworks);
  }

  function handleDragStart(event) {
    const { active } = event;
    setActiveId(active.id);
  }

  function handleDragEnd(event) {
    const { active, over } = event;

    setActiveId(null);

    if (active?.id !== over?.id) {
      const oldIndex = cards.findIndex((artwork) => artwork._id === active.id);
      const newIndex = cards.findIndex((artwork) => artwork._id === over.id);

      handleSortEnd(arrayMove(cards, oldIndex, newIndex));
    }
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={cards.map((item) => item._id)}
        strategy={verticalListSortingStrategy}
      >
        <AutoSizer>
          {({ width, height }) => (
            <List
              width={width}
              height={height}
              className="remove-scrollbar"
              overscanRowCount={3}
              rowCount={cards.length}
              rowHeight={screenSize.width <= 550 ? 63 : 65}
              rowRenderer={({ index, key, style }) => (
                <ArtworkRow
                  key={key}
                  style={style}
                  item={cards[index]}
                  deleteItem={() => deleteItem(cards[index])}
                />
              )}
            />
          )}
        </AutoSizer>
      </SortableContext>

      <DragOverlay>
        {activeId ? (
          <ArtworkRow
            item={cards.find((item) => item._id === activeId)}
            isActive
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
